/**
 * Hybrid Model Selection System
 * 
 * Updated to use INT8 model for both options (INT4 quantization not available)
 * 
 * Features:
 * - Storage quota detection
 * - Automatic model version selection
 * - Graceful fallback handling
 * - Cache management with versioning
 */

// ============================================================================
// PART 1: Model Configuration
// ============================================================================

export interface ModelConfig {
  name: string;
  url: string;  // Will be replaced with signed URL at runtime
  size: number; // in bytes
  quantization: 'INT8' | 'INT4' | 'Q4F16' | 'Q4';
  version: string;
  modelId: string;  // NEW: Identifier for backend to know which model
  runtime: 'webllm' | 'transformers.js';  // Which runtime to use
}

export type LocalModelTier = 'high-local' | 'low-local' | 'tiny-local' | 'unsupported-local';
export type BackendPromptDeviceType = 'desktop' | 'mobile';
export type LocalPromptProfile = 'standard' | 'compact' | 'tiny';

export interface LocalDeviceRiskFlags {
  isMacLike?: boolean;
  isIntelAdapter?: boolean;
  isLikelyLegacyIntelMac?: boolean;
  veryLowReportedMemory?: boolean;
  lowCoreCount?: boolean;
  smallWebGpuBuffer?: boolean;
  smallStorageBinding?: boolean;
  lowLocalBlocked?: boolean;
}

export interface LocalModelCapability {
  tier: LocalModelTier;
  supported: boolean;
  backendDeviceType: BackendPromptDeviceType | null;
  modelId: 'qwen3.5-4b-q4f16-webllm' | 'qwen3.5-2b-q4f16-webllm' | 'qwen3.5-0.8b-q4f16-webllm' | null;
  runtime: 'qwen3' | 'mobile_qwen3' | 'tiny_qwen3' | null;
  reason: string;
  isMobile: boolean;
  isIOS: boolean;
  browser: 'safari' | 'chromium' | 'firefox' | 'unknown';
  adapterInfo?: {
    vendor?: string;
    architecture?: string;
    description?: string;
    device?: string;
    isFallbackAdapter?: boolean;
  };
  limits?: Record<string, number>;
  features?: string[];
  deviceMemoryGB?: number;
  hardwareConcurrency?: number;
  promptProfile?: LocalPromptProfile;
  riskFlags?: LocalDeviceRiskFlags;
  platform?: string;
  devicePixelRatio?: number;
  viewport?: { width: number; height: number };
}

export const MODEL_CONFIGS: Record<string, ModelConfig> = {
  INT8: {
    name: 'SmolLM2-1.7B-INT8',
    url: 'placeholder-int8',
    size: 1634 * 1024 * 1024,
    quantization: 'INT8',
    version: 'v2-int8',
    modelId: 'smollm2-int8',
    runtime: 'webllm'
  },
  INT4: {
    name: 'SmolLM2-1.7B-INT4',
    url: 'placeholder-int4',
    size: 1634 * 1024 * 1024,
    quantization: 'INT4',
    version: 'v2-int4',
    modelId: 'smollm2-int4',
    runtime: 'webllm'
  },
  TINY_QWEN3_Q4F16: {
    name: 'Qwen3.5-0.8B-Q4F16-WebLLM',
    url: 'placeholder-qwen3.5-0.8b-q4f16',
    size: 447 * 1024 * 1024, // ~447 MB for Qwen3.5-0.8B MLC folder
    quantization: 'Q4F16',
    version: 'v1-qwen3.5-0.8b',
    modelId: 'qwen3.5-0.8b-q4f16-webllm',
    runtime: 'webllm'
  },
  MOBILE_QWEN3_Q4F16: {
    name: 'Qwen3.5-2B-Q4F16-WebLLM',
    url: 'placeholder-q4f16',
    size: 880 * 1024 * 1024, // ~880 MB for Qwen3.5-2B
    quantization: 'Q4F16',
    version: 'v1-qwen3.5-2b',
    modelId: 'qwen3.5-2b-q4f16-webllm',
    runtime: 'webllm'
  },
  QWEN3_Q4F16: {
    name: 'Qwen3.5-4B-Q4F16-WebLLM',
    url: 'placeholder-qwen3-q4f16',
    size: 2390 * 1024 * 1024,   // ~2.39 GB for Qwen3.5-4B
    quantization: 'Q4F16',
    version: 'v1-qwen3.5-4b',
    modelId: 'qwen3.5-4b-q4f16-webllm',
    runtime: 'webllm'            // CHANGED: now using WebLLM, not transformers.js
  }
};

// ============================================================================
// PART 1.5: Device + WebGPU Capability Detection
// ============================================================================

export function detectBrowser(): LocalModelCapability['browser'] {
  const ua = navigator.userAgent || '';
  if (/Firefox|FxiOS/i.test(ua)) return 'firefox';
  if (/Chrome|CriOS|Chromium|Edg\//i.test(ua)) return 'chromium';
  if (/Safari/i.test(ua)) return 'safari';
  return 'unknown';
}

export function isIOSLike(): boolean {
  const ua = navigator.userAgent || '';
  const platform = (navigator as any).userAgentData?.platform || navigator.platform || '';

  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    // iPadOS can report as MacIntel while having touch points.
    (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function isMobileDevice(): boolean {
  const nav = navigator as any;

  if (nav.userAgentData && typeof nav.userAgentData.mobile === 'boolean') {
    return nav.userAgentData.mobile;
  }

  const ua = navigator.userAgent || '';
  const ios = isIOSLike();
  const android = /Android/i.test(ua);
  const smallTouch = window.innerWidth < 1024 && navigator.maxTouchPoints > 0;

  return ios || android || smallTouch;
}

function unsupportedLocal(
  reason: string,
  base: Pick<LocalModelCapability, 'isMobile' | 'isIOS' | 'browser' | 'deviceMemoryGB' | 'hardwareConcurrency'>,
): LocalModelCapability {
  return {
    tier: 'unsupported-local',
    supported: false,
    backendDeviceType: null,
    modelId: null,
    runtime: null,
    reason,
    ...base,
  };
}

function normalizeSupportedLimits(limits: any): Record<string, number> {
  const out: Record<string, number> = {};

  if (!limits || typeof limits !== 'object') {
    return out;
  }

  for (const key of Object.keys(limits)) {
    const value = limits[key];
    if (typeof value === 'number') {
      out[key] = value;
    }
  }

  return out;
}

function highLocalBlocked(): boolean {
  const until = Number(localStorage.getItem('qwen3HighBlockedUntil') || 0);
  return Number.isFinite(until) && until > Date.now();
}

export function blockHighLocalForOneDay(reason: string): void {
  localStorage.setItem('qwen3HighBlockedUntil', String(Date.now() + 24 * 60 * 60 * 1000));
  localStorage.setItem('qwen3HighBlockedReason', reason);
}

function lowLocalBlocked(): boolean {
  const until = Number(localStorage.getItem('qwen3LowLocalBlockedUntil') || 0);
  return Number.isFinite(until) && until > Date.now();
}

export function blockLowLocalForOneDay(reason: string): void {
  localStorage.setItem('qwen3LowLocalBlockedUntil', String(Date.now() + 24 * 60 * 60 * 1000));
  localStorage.setItem('qwen3LowLocalBlockedReason', reason);
}

function tinyLocalBlocked(): boolean {
  const until = Number(localStorage.getItem('qwen3TinyLocalBlockedUntil') || 0);
  return Number.isFinite(until) && until > Date.now();
}

export function blockTinyLocalForOneDay(reason: string): void {
  localStorage.setItem('qwen3TinyLocalBlockedUntil', String(Date.now() + 24 * 60 * 60 * 1000));
  localStorage.setItem('qwen3TinyLocalBlockedReason', reason);
}

export async function getLocalModelCapability(): Promise<LocalModelCapability> {
  // Hardcoded Low-Local Override (6.3.26)
  if (typeof window !== 'undefined' && window.localStorage.getItem('forceLowLocalQwen') === '1') {
    return {
      supported: true,
      tier: 'low-local',
      runtime: 'mobile_qwen3',
      backendDeviceType: 'mobile',
      modelId: 'qwen3.5-2b-q4f16-webllm',
      reason: 'DEV OVERRIDE: forcing low-local Qwen3.5-2B path for testing.',
      browser: 'chromium',
      isMobile: false,
      isIOS: false,
      adapterInfo: {},
      limits: {},
      features: [],
      deviceMemoryGB: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      promptProfile: 'standard',
      riskFlags: {},
      platform: String((navigator as any).userAgentData?.platform || navigator.platform || 'unknown'),
      devicePixelRatio: window.devicePixelRatio || 1,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    } as LocalModelCapability;
  }

  // Hardcoded Tiny Override for testing Qwen3.5-0.8B path
  if (typeof window !== 'undefined' && window.localStorage.getItem('forceTinyLocalQwen') === '1') {
    return {
      supported: true,
      tier: 'tiny-local',
      runtime: 'tiny_qwen3',
      backendDeviceType: 'mobile',
      modelId: 'qwen3.5-0.8b-q4f16-webllm',
      reason: 'DEV OVERRIDE: forcing tiny-local Qwen3.5-0.8B path for testing.',
      browser: detectBrowser(),
      isMobile: isMobileDevice(),
      isIOS: isIOSLike(),
      adapterInfo: {},
      limits: {},
      features: [],
      deviceMemoryGB: (navigator as any).deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      promptProfile: 'tiny',
      riskFlags: {
        isMacLike: /mac/i.test(String((navigator as any).userAgentData?.platform || navigator.platform || '')),
        isIntelAdapter: false,
        isLikelyLegacyIntelMac: false,
        veryLowReportedMemory: false,
        lowCoreCount: false,
        smallWebGpuBuffer: false,
        smallStorageBinding: false,
        lowLocalBlocked: false,
      },
      platform: String((navigator as any).userAgentData?.platform || navigator.platform || 'unknown'),
      devicePixelRatio: window.devicePixelRatio || 1,
      viewport: { width: window.innerWidth, height: window.innerHeight },
    } as LocalModelCapability;
  }
  
  const isMobile = isMobileDevice();
  const isIOS = isIOSLike();
  const browser = detectBrowser();
  const deviceMemoryGB = (navigator as any).deviceMemory as number | undefined;
  const hardwareConcurrency = navigator.hardwareConcurrency;
  const platform = String((navigator as any).userAgentData?.platform || navigator.platform || 'unknown');
  const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const viewport = typeof window !== 'undefined'
    ? { width: window.innerWidth, height: window.innerHeight }
    : undefined;

  const base = { isMobile, isIOS, browser, deviceMemoryGB, hardwareConcurrency, platform, devicePixelRatio, viewport };

  if (!window.isSecureContext) {
    return unsupportedLocal('WebGPU requires HTTPS / secure context.', base);
  }

  if (!('gpu' in navigator) || !(navigator as any).gpu) {
    return unsupportedLocal(
      isIOS
        ? 'This iOS/Safari version does not expose WebGPU, so local Enlive AI is not supported.'
        : 'This browser does not expose navigator.gpu, so local Enlive AI is not supported.',
      base,
    );
  }

  let adapter: any = null;
  try {
    adapter = await (navigator as any).gpu.requestAdapter({
      powerPreference: 'high-performance',
    });
  } catch (err) {
    return unsupportedLocal(
      `WebGPU adapter request failed: ${err instanceof Error ? err.message : String(err)}`,
      base,
    );
  }

  if (!adapter) {
    return unsupportedLocal('WebGPU is present, but no GPU adapter was returned.', base);
  }

  const info = adapter.info || {};
  const rawLimits = adapter.limits || {};
  const limits = normalizeSupportedLimits(rawLimits);
  const features = Array.from(adapter.features || []) as string[];
  const adapterText = `${info.vendor || ''} ${info.architecture || ''} ${info.description || ''}`.toLowerCase();

  if (info.isFallbackAdapter) {
    return unsupportedLocal('WebGPU returned a fallback adapter.', base);
  }

  const isIntel = /intel|uhd|iris/.test(adapterText);
  const isAppleGpu = /apple|metal/.test(adapterText);
  const isDiscreteLike = /nvidia|amd|radeon|geforce|rtx|rx\b/.test(adapterText);
  const lowCore = typeof hardwareConcurrency === 'number' && hardwareConcurrency <= 4;
  const lowMemory = typeof deviceMemoryGB === 'number' && deviceMemoryGB < 12;
  const smallBufferLimit = typeof limits.maxBufferSize === 'number' && limits.maxBufferSize <= 268_435_456;
  const smallStorageBinding =
    typeof limits.maxStorageBufferBindingSize === 'number' &&
    limits.maxStorageBufferBindingSize <= 134_217_728;

  const common = {
    ...base,
    adapterInfo: info,
    limits,
    features,
  };

  const isMacLike = /mac/i.test(platform) || /macintosh|mac os x/i.test(navigator.userAgent || '');
  const veryLowReportedMemory = typeof deviceMemoryGB === 'number' && deviceMemoryGB <= 8;
  const lowCoreCount = typeof hardwareConcurrency === 'number' && hardwareConcurrency <= 4;
  const isLikelyLegacyIntelMac = Boolean(
    isMacLike && isIntel && (veryLowReportedMemory || lowCoreCount || smallBufferLimit || smallStorageBinding)
  );

  const riskFlags: LocalDeviceRiskFlags = {
    isMacLike,
    isIntelAdapter: isIntel,
    isLikelyLegacyIntelMac,
    veryLowReportedMemory,
    lowCoreCount,
    smallWebGpuBuffer: smallBufferLimit,
    smallStorageBinding,
    lowLocalBlocked: lowLocalBlocked(),
  };

  const promptProfile: LocalPromptProfile = isLikelyLegacyIntelMac
    ? 'tiny'
    : (isIntel || lowCore || lowMemory || smallBufferLimit || smallStorageBinding ? 'compact' : 'standard');

  if (tinyLocalBlocked()) {
    return unsupportedLocal(
      `Tiny-local Qwen3.5 was temporarily disabled after degenerate output: ${localStorage.getItem('qwen3TinyLocalBlockedReason') || 'previous failure'}.`,
      base,
    );
  }

    const shouldUseTinyLocal = Boolean(
    lowLocalBlocked() ||
    isLikelyLegacyIntelMac ||
    veryLowReportedMemory ||
    smallBufferLimit ||
    smallStorageBinding ||
    (isMobile && typeof deviceMemoryGB === 'number' && deviceMemoryGB <= 4)
  );

  if (shouldUseTinyLocal) {
    const blockedReason = lowLocalBlocked()
      ? ` Low-local 2B previously blocked: ${localStorage.getItem('qwen3LowLocalBlockedReason') || 'previous 2B failure'}.`
      : '';

    return {
      tier: 'tiny-local',
      supported: true,
      backendDeviceType: 'mobile',
      modelId: 'qwen3.5-0.8b-q4f16-webllm',
      runtime: 'tiny_qwen3',
      reason:
        `Tiny-local selected: legacyIntelMac=${isLikelyLegacyIntelMac}, veryLowMemory=${veryLowReportedMemory}, ` +
        `smallBuffer=${smallBufferLimit}, smallStorageBinding=${smallStorageBinding}, promptProfile=tiny.` + blockedReason,
      promptProfile: 'tiny',
      riskFlags,
      ...common,
    };
  }

  if (
    isMobile ||
    isIOS ||
    isIntel ||
    lowCore ||
    lowMemory ||
    smallBufferLimit ||
    smallStorageBinding ||
    highLocalBlocked()
  ) {
    const blockedReason = highLocalBlocked()
      ? ` High-local temporarily blocked: ${localStorage.getItem('qwen3HighBlockedReason') || 'previous failure'}.`
      : '';

    return {
      tier: 'low-local',
      supported: true,
      backendDeviceType: 'mobile',
      modelId: 'qwen3.5-2b-q4f16-webllm',
      runtime: 'mobile_qwen3',
      reason:
        `Low-local selected: mobile=${isMobile}, ios=${isIOS}, intel=${isIntel}, ` +
        `lowCore=${lowCore}, lowMemory=${lowMemory}, smallBuffer=${smallBufferLimit}, ` +
        `smallStorageBinding=${smallStorageBinding}, promptProfile=${promptProfile}.` + blockedReason,
      promptProfile,
      riskFlags,
      ...common,
    };
  }

  if (isAppleGpu || isDiscreteLike) {
    return {
      tier: 'high-local',
      supported: true,
      backendDeviceType: 'desktop',
      modelId: 'qwen3.5-4b-q4f16-webllm',
      runtime: 'qwen3',
      reason: 'High-local selected after WebGPU adapter and coarse device checks.',
      promptProfile: 'standard',
      riskFlags,
      ...common,
    };
  }

  return {
    tier: 'low-local',
    supported: true,
    backendDeviceType: 'mobile',
    modelId: 'qwen3.5-2b-q4f16-webllm',
    runtime: 'mobile_qwen3',
    reason: `WebGPU is available, but adapter capability was not clearly high-end; defaulting to low-local with promptProfile=${promptProfile}.`,
    promptProfile,
    riskFlags,
    ...common,
  };
}

// ============================================================================
// PART 2: Storage Quota Detection
// ============================================================================

export interface StorageQuotaInfo {
  available: number;
  total: number;
  used: number;
  canFitINT8: boolean;
  canFitINT4: boolean;
  recommendedModel: 'INT8' | 'INT4' | null;
}

export async function checkStorageQuota(): Promise<StorageQuotaInfo> {
  try {
    if (!navigator.storage || !navigator.storage.estimate) {
      console.warn('⚠️  Storage API not supported');
      console.log('ℹ️  Proceeding optimistically - will handle quota errors if they occur');
      return {
        available: Infinity,
        total: Infinity,
        used: 0,
        canFitINT8: true,
        canFitINT4: true,
        recommendedModel: 'INT8'
      };
    }

    // Request persistent storage (helps in some browsers)
    if (navigator.storage.persist) {
      await navigator.storage.persist();
    }

    const estimate = await navigator.storage.estimate();
    let quota = estimate.quota || 0;
    const usage = estimate.usage || 0;
    
    // Log raw values for debugging
    console.log('📊 Raw Storage Info:');
    console.log(`   Reported quota: ${(quota / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`   Used: ${(usage / 1024 / 1024).toFixed(2)} MB`);

    // UNIVERSAL FIX: If quota seems unreasonably low, be optimistic
    const MIN_REASONABLE_QUOTA = 3 * 1024 * 1024 * 1024; // 3 GB
    
    if (quota < MIN_REASONABLE_QUOTA) {
      console.warn('⚠️  Browser reported low quota:', (quota / 1024 / 1024 / 1024).toFixed(2), 'GB');
      console.log('ℹ️  Modern browsers allocate storage dynamically');
      console.log('ℹ️  Proceeding with download - browser will allocate more if needed');
      console.log('ℹ️  If quota is truly exceeded, we\'ll catch the error and handle it');
      
      return {
        available: Infinity,
        total: quota,
        used: usage,
        canFitINT8: true,
        canFitINT4: true,
        recommendedModel: 'INT8'
      };
    }

    // If quota seems reasonable, do normal check
    const available = quota - usage;
    const SAFETY_MARGIN = 0.90; // 90%
    const safeAvailable = available * SAFETY_MARGIN;

    const canFitINT8 = safeAvailable >= MODEL_CONFIGS.INT8.size;
    const canFitINT4 = canFitINT8;

    let recommendedModel: 'INT8' | 'INT4' | null = null;
    if (canFitINT8) {
      recommendedModel = 'INT8';
    }

    console.log('📊 Storage Quota Analysis:');
    console.log(`   Total: ${(quota / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`   Used: ${(usage / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`   Available: ${(available / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`   Safe Available (90%): ${(safeAvailable / 1024 / 1024 / 1024).toFixed(2)} GB`);
    console.log(`   Can fit INT8 (1.7GB): ${canFitINT8 ? '✅' : '❌'}`);
    console.log(`   Recommended: ${recommendedModel || 'Proceeding optimistically'}`);

    return {
      available: safeAvailable,
      total: quota,
      used: usage,
      canFitINT8,
      canFitINT4,
      recommendedModel: recommendedModel || 'INT8'
    };

  } catch (error) {
    console.error('❌ Failed to check storage quota:', error);
    console.log('ℹ️  Proceeding optimistically');
    
    return {
      available: Infinity,
      total: 0,
      used: 0,
      canFitINT8: true,
      canFitINT4: true,
      recommendedModel: 'INT8'
    };
  }
}

// ============================================================================
// PART 3: Model Selection Logic
// ============================================================================

export interface ModelSelectionResult {
  config: ModelConfig;
  reason: string;
  isPreferred: boolean;
}

/**
 * Select the best model based on storage availability
 * 
 * Note: Both INT8 and INT4 options now use the same INT8 model
 * Priority:
 * 1. NEW Top Dawg: Q4F16 WebLLM option
 * 2. INT8 if storage permits
 * 3. INT4 (same as INT8) as fallback
 * 4. Throw error if insufficient storage
 */
export async function selectBestModel(): Promise<ModelSelectionResult> {
  console.log('🔍 Selecting best model for this device...');
  
  const quotaInfo = await checkStorageQuota();
  const capability = await getLocalModelCapability();

  if (capability.tier === 'high-local') {
    console.log('Selected high-local Qwen3.5-4B Q4F16 via WebLLM/MLC');
    return {
      config: MODEL_CONFIGS.QWEN3_Q4F16,
      reason: capability.reason,
      isPreferred: true,
    };
  }

  if (capability.tier === 'tiny-local') {
    console.log('Selected tiny-local Qwen3.5-0.8B Q4F16 via WebLLM/MLC');
    return {
      config: MODEL_CONFIGS.TINY_QWEN3_Q4F16,
      reason: capability.reason,
      isPreferred: true,
    };
  }

  if (capability.tier === 'low-local') {
    console.log('Selected low-local Qwen3.5-2B Q4F16 via WebLLM/MLC');
    return {
      config: MODEL_CONFIGS.MOBILE_QWEN3_Q4F16,
      reason: capability.reason,
      isPreferred: true,
    };
  }

  throw new Error(`LOCAL_WEBLLM_UNSUPPORTED: ${capability.reason}`);
}


// ============================================================================
// PART 4: Cache Management with Versioning
// ============================================================================

const CACHE_NAME = 'enlivesystems-models-v5'; // Incremented cache version for new Qwen3.5 model

/**
 * Check if a specific model version is cached
 */
export async function isModelCached(config: ModelConfig): Promise<boolean> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const cacheKey = `${config.url}?v=${config.version}`;
    const cachedResponse = await cache.match(cacheKey);
    return cachedResponse !== undefined;
  } catch (error) {
    console.error('❌ Failed to check cache:', error);
    return false;
  }
}

/**
 * Helper function to handle cache storage with quota error handling
 */
async function tryCacheBlob(
  cache: Cache,
  cacheKey: string,
  blob: Blob
): Promise<void> {
  console.log('💾 Attempting to cache model for future use...');
  try {
    await cache.put(cacheKey, new Response(blob));
    console.log('✅ Model cached successfully!');
  } catch (cacheError: any) {
    // Handle quota exceeded error gracefully
    if (cacheError.name === 'QuotaExceededError' || 
        cacheError.message?.includes('quota') ||
        cacheError.message?.includes('Quota')) {
      console.error('❌ Storage quota exceeded - cannot cache model');
      console.warn('⚠️  Model will work this session but won\'t be cached');
      console.warn('⚠️  To fix: Clear browser data or use a browser with more storage');
      
      // Try to clear old caches to make room
      console.log('🧹 Attempting to clear old caches...');
      try {
        const clearedSpace = await clearOldCaches();
        if (clearedSpace) {
          // Retry caching after cleanup
          await cache.put(cacheKey, new Response(blob.slice(0)));
          console.log('✅ Model cached successfully after cleanup!');
        } else {
          console.warn('⚠️  No old caches to clear');
          console.warn('⚠️  Continuing without cache - model will work this session');
        }
      } catch (retryError) {
        console.error('❌ Still cannot cache after cleanup:', retryError);
        console.warn('⚠️  Continuing without cache - model will work this session');
      }
    } else {
      // Other caching error (not quota-related)
      console.error('⚠️  Failed to cache model:', cacheError);
      console.warn('⚠️  Model will work but won\'t be cached for next time');
    }
    // Don't throw - model still works, just not cached
  }
}

/**
 * Cache model with progress tracking and quota error handling
 */
export async function cacheModel(
  config: ModelConfig,
  onProgress?: (loaded: number, total: number, percentage: number) => void
): Promise<Blob> {
  const cacheKey = `${config.url}?v=${config.version}`;
  
  try {
    const cache = await caches.open(CACHE_NAME);
    
    // ========================================
    // Step 1: Check if model is already cached
    // ========================================
    console.log(`🔍 Checking cache for ${config.name}...`);
    const cachedResponse = await cache.match(cacheKey);
    
    if (cachedResponse) {
      console.log(`✅ ${config.name} found in cache! Loading from disk...`);
      const startTime = performance.now();
      const blob = await cachedResponse.blob();
      const loadTime = ((performance.now() - startTime) / 1000).toFixed(1);
      
      console.log(`✅ Model loaded from cache in ${loadTime}s`);
      console.log(`📦 Model size: ${(blob.size / 1024 / 1024).toFixed(0)} MB`);
      
      return blob;
    }
    
    // ========================================
    // Step 2: Cache MISS - Download the model
    // ========================================
    console.log(`❌ ${config.name} not in cache. Downloading...`);
    console.log(`⬇️  Downloading ${config.name} (${(config.size / 1024 / 1024).toFixed(0)} MB)...`);
    
    const response = await fetch(config.url);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    // Get total size
    const contentLength = response.headers.get('Content-Length');
    const totalBytes = contentLength ? parseInt(contentLength, 10) : config.size;
    
    console.log(`📊 Total size: ${(totalBytes / 1024 / 1024).toFixed(0)} MB`);
    
    // ========================================
    // Step 3: Download with progress tracking
    // ========================================
    let blob: Blob;
    
    if (response.body && onProgress) {
      // Stream with progress
      const reader = response.body.getReader();
      let receivedBytes = 0;
      const chunks: BlobPart[] = [];
      let lastLoggedPercentage = 0;
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;
        
        const copied = value.buffer.slice(
          value.byteOffset,
          value.byteOffset + value.byteLength
        ) as ArrayBuffer;
        chunks.push(copied);
        receivedBytes += value.byteLength;
        
        // Report progress
        const percentage = (receivedBytes / totalBytes) * 100;
        onProgress(receivedBytes, totalBytes, percentage);
        
        // Log every 10%
        const currentPercentage = Math.floor(percentage / 10) * 10;
        if (currentPercentage > lastLoggedPercentage && currentPercentage > 0) {
          console.log(`📥 Downloaded: ${percentage.toFixed(1)}% (${(receivedBytes / 1024 / 1024).toFixed(0)} MB / ${(totalBytes / 1024 / 1024).toFixed(0)} MB)`);
          lastLoggedPercentage = currentPercentage;
        }
      }
      
      // Combine chunks into blob
      blob = new Blob(chunks);
    } else {
      // No progress tracking - simple download
      blob = await response.blob();
    }
    
    // ========================================
    // Step 4: Cache for next time (unified logic)
    // ========================================
    await tryCacheBlob(cache, cacheKey, blob);
    
    return blob;
    
  } catch (error) {
    console.error('❌ Cache API failed:', error);
    console.warn('⚠️  Falling back to direct fetch (no caching)');
    
    // Fallback: direct fetch without caching
    const response = await fetch(config.url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.blob();
  }
}

/**
 * Helper function to clear old cache versions
 * Returns true if any caches were cleared, false otherwise
 */
export async function clearOldCaches(): Promise<boolean> {
  try {
    const cacheNames = await caches.keys();
    
    // Find old cache versions (anything that starts with our prefix but isn't current)
    const oldCaches = cacheNames.filter(name => 
      name.startsWith('enlivesystems-models-') && name !== CACHE_NAME
    );
    
    if (oldCaches.length === 0) {
      return false;
    }
    
    // Delete old caches
    for (const cacheName of oldCaches) {
      await caches.delete(cacheName);
      console.log(`🗑️  Deleted old cache: ${cacheName}`);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Failed to clear old caches:', error);
    return false;
  }
}

/**
 * Clear all cached models
 */
export async function clearModelCache(): Promise<void> {
  try {
    const deleted = await caches.delete(CACHE_NAME);
    if (deleted) {
      console.log('✅ Model cache cleared');
    } else {
      console.log('ℹ️  No cache to clear');
    }
  } catch (error) {
    console.error('❌ Failed to clear cache:', error);
  }
}

// ============================================================================
// PART 5: Request Persistent Storage (Optional but Recommended)
// ============================================================================

/**
 * Request persistent storage to prevent browser from evicting cache
 */
export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage || !navigator.storage.persist) {
      console.warn('⚠️  Persistent storage API not supported');
      return false;
    }

    const isPersisted = await navigator.storage.persist();
    
    if (isPersisted) {
      console.log('✅ Storage marked as persistent');
    } else {
      console.log('⚠️  Storage persistence request denied (may be evicted under pressure)');
    }
    
    return isPersisted;
  } catch (error) {
    console.error('❌ Failed to request persistent storage:', error);
    return false;
  }
}

/**
 * Check if storage is already persistent
 */
export async function isStoragePersistent(): Promise<boolean> {
  try {
    if (!navigator.storage || !navigator.storage.persisted) {
      return false;
    }

    return await navigator.storage.persisted();
  } catch (error) {
    console.error('❌ Failed to check storage persistence:', error);
    return false;
  }
}

// ============================================================================
// PART 6: Signed URL Configuration
// ============================================================================

/**
 * Get model config with signed URL from backend
 */
export async function getModelConfigWithSignedUrl(
  modelKey: string
): Promise<ModelConfig> {
  const config = MODEL_CONFIGS[modelKey];
  
  if (!config) {
    throw new Error(`Unknown model key: ${modelKey}`);
  }
  
  // Get signed URLs from backend
  const urlsResponse = await getSignedModelUrls(config.modelId);
  
  // Return config with signed model URL
  return {
    ...config,
    url: urlsResponse.modelUrl  // Extract the string URL from the response object
  };
}

/**
 * Model URLs response from backend
 */
export interface ModelUrlsResponse {
  modelUrl: string;
  wasmUrl: string;
  expiresIn: number;
  configUrl: string;           
  tokenizerUrl: string;        
  tokenizerConfigUrl: string;  
  ndarrayCacheUrl: string;
  remainingDownloads: number;
  modelId: string;
}

/**
 * Request signed model and WASM URLs from the backend
 */
export async function getSignedModelUrls(modelId?: string): Promise<ModelUrlsResponse> {
  try {
    console.log(`🔑 Requesting signed model URLs from backend (model: ${modelId || 'default'})...`);
    
    const response = await fetch('/get-model-urls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',  // Send cookies
      body: JSON.stringify({ modelId: modelId || 'qwen3.5-4b-q4f16-webllm' }),
    });

    if (!response.ok) {
      if (response.status === 429) {
        const data = await response.json();
        throw new Error(`Rate limit exceeded: ${data.message}`);
      }
      throw new Error(`Failed to get model URLs: ${response.statusText}`);
    }

    const data: ModelUrlsResponse = await response.json();
    
    console.log(`✅ Received signed URLs, expires in ${data.expiresIn}s`);
    console.log(`📊 Remaining downloads today: ${data.remainingDownloads}`);
    
    return data;
    
  } catch (error) {
    console.error('❌ Failed to get signed model URLs:', error);
    throw error;
  }
}

/**
 * Cache signed URLs with expiry tracking
 */
interface CachedModelUrls {
  modelUrl: string;
  wasmUrl: string;
  configUrl: string;       
  tokenizerUrl: string;       
  tokenizerConfigUrl: string; 
  ndarrayCacheUrl: string;  
  expiresAt: number;  // Unix timestamp
  modelId: string;
}

let cachedModelUrls: CachedModelUrls | null = null;

/**
 * Get model URLs with caching
 */
export async function getModelUrls(modelId?: string): Promise<ModelUrlsResponse> {
  const now = Date.now();
  
  // Use a default model ID if none is provided, but now defaulting to Qwen3.5 2B
  const effectiveModelId = modelId || 'qwen3.5-2b-q4f16-webllm';
  
  // Check if we have valid cached URLs FOR THE SAME MODEL
  if (cachedModelUrls && cachedModelUrls.expiresAt > now && cachedModelUrls.modelId === effectiveModelId) {
    console.log('✅ Using cached model URLs');
    return {
      modelUrl: cachedModelUrls.modelUrl,
      wasmUrl: cachedModelUrls.wasmUrl,
      configUrl: cachedModelUrls.configUrl,      
      tokenizerUrl: cachedModelUrls.tokenizerUrl,     
      tokenizerConfigUrl: cachedModelUrls.tokenizerConfigUrl, 
      ndarrayCacheUrl: cachedModelUrls.ndarrayCacheUrl,
      expiresIn: Math.floor((cachedModelUrls.expiresAt - now) / 1000),
      remainingDownloads: -1,  // Unknown from cache
      modelId: cachedModelUrls.modelId
    };
  }
  
  // Request new signed URLs
  console.log('🔄 Requesting fresh signed URLs...');
  const urlsResponse = await getSignedModelUrls(effectiveModelId);
  
  // Cache for 1.5 hours (leave 30 min buffer before 2-hour expiry)
  cachedModelUrls = {
    modelUrl: urlsResponse.modelUrl,
    wasmUrl: urlsResponse.wasmUrl,
    configUrl: urlsResponse.configUrl,         
    tokenizerUrl: urlsResponse.tokenizerUrl,    
    tokenizerConfigUrl: urlsResponse.tokenizerConfigUrl, 
    ndarrayCacheUrl: urlsResponse.ndarrayCacheUrl, 
    expiresAt: now + (1.5 * 60 * 60 * 1000),
    modelId: urlsResponse.modelId
  };
  
  return urlsResponse;
}

/**
 * Aggressively clear ALL model caches, including WebLLM's internal caches.
 * Call this when a user deletes their account.
 */
export async function purgeAllModelData(): Promise<void> {
  console.log('🧹 Purging ALL model data from browser storage...');
  try {
    // 1. Clear our custom cache
    await caches.delete(CACHE_NAME);
    console.log(`✅ Deleted custom cache: ${CACHE_NAME}`);

    // 2. Clear WebLLM's internal caches
    const cacheNames = await caches.keys();
    for (const name of cacheNames) {
      // WebLLM typically uses 'webllm/model' or similar names
      if (name.includes('webllm') || name.includes('enlivesystems-models')) {
        await caches.delete(name);
        console.log(`✅ Deleted WebLLM/model cache: ${name}`);
      }
    }

    // 3. Clear IndexedDB databases used by WebLLM (if any)
    // WebLLM sometimes uses IndexedDB for metadata
    if (window.indexedDB && window.indexedDB.databases) {
      const dbs = await window.indexedDB.databases();
      for (const db of dbs) {
        if (db.name && (db.name.includes('webllm') || db.name.includes('tvm'))) {
          window.indexedDB.deleteDatabase(db.name);
          console.log(`✅ Deleted IndexedDB: ${db.name}`);
        }
      }
    }

    console.log('✅ All model data successfully purged from browser.');
  } catch (error) {
    console.error('❌ Failed to purge model data:', error);
  }
}